/**
 * TemplateGallery — search bar, result count, and the
 * grid / split-list rendering of the template library.
 *
 * Extracted from BlogTemplatesPage (Phase 1 decomposition). Adjudicated
 * change: the All/System/My Tabs are replaced by labeled GROUPS on one
 * page. S3 (CONSULTS §1): the GRID is grouped BY RECIPE CLASS with class
 * headers + fitness-first descriptions — COMPACT (served profile_class
 * short_form) and STANDARD (all else; missing/malformed floor payloads
 * degrade to Standard). Ownership is per-card (YOURS chip); within each
 * class the parent's ordering is preserved (Yours-first). Split-list mode
 * keeps the Yours/Standard grouping (S4 owns the detail-pane overlay).
 *
 * Presentational only: the parent composes the lists (via
 * useTemplateFilters) and wires all callbacks.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import loadingAnimation from '@/assets/loading-animation.webp';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X, FileText, Zap } from 'lucide-react';
import { blogTemplatesService, BlogTemplateSummary } from '@/services/blogTemplatesService';
import { TemplateCard } from './TemplateCard';
import { TemplateListItem } from './TemplateListItem';
import { groupByGalleryClass } from './shape/galleryClass';
import { GALLERY_STRINGS } from './galleryStrings';

export interface TemplateGalleryProps {
  /** Tenant-owned templates (!is_system), filtered + sorted by the parent. */
  yoursTemplates: BlogTemplateSummary[];
  /** Platform-provided templates (is_system), filtered + sorted by the parent. */
  standardTemplates: BlogTemplateSummary[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;

  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;

  /** Split mode (400px list + inspector) is driven by an active selection. */
  splitMode: boolean;
  selectedTemplateId: string | null;
  onSelect: (template: BlogTemplateSummary) => void;
  onUse: (template: BlogTemplateSummary) => void;
  /** W2 — card overflow "Expand shape" → drawer host (optional: grid stays
   *  drop-in where no drawer host exists). */
  onExpandShape?: (template: BlogTemplateSummary) => void;
  onCustomize: (template: BlogTemplateSummary) => void;
  onDelete: (template: BlogTemplateSummary) => void;
  onCreateTemplate: () => void;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  yoursTemplates,
  standardTemplates,
  isLoading,
  error,
  onRetry,
  searchQuery,
  onSearchChange,
  onClearFilters,
  splitMode,
  selectedTemplateId,
  onSelect,
  onUse,
  onExpandShape,
  onCustomize,
  onDelete,
  onCreateTemplate,
}) => {
  const totalCount = yoursTemplates.length + standardTemplates.length;

  // Rotating loading messages (grid-mode loader)
  const LOADING_MESSAGES = useMemo(() => [
    'Loading template library...',
    'Fetching templates from the database...',
    'Organizing your template library...',
    'Preparing your template options...',
    'Almost ready...',
  ], []);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setLoadingMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isLoading, LOADING_MESSAGES]);

  const renderGroupHeader = (label: string, count: number, fitness?: string) => (
    <div className="flex items-center gap-2 mb-2">
      <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {label} ({count})
      </h3>
      {fitness && (
        <span data-class-fitness="true" className="text-xs text-muted-foreground">
          {fitness}
        </span>
      )}
      <div className="flex-1 border-t border-border" />
    </div>
  );

  const renderCardGrid = (templates: BlogTemplateSummary[]) => (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {templates.map((template) => (
        <TemplateCard
          key={template.id}
          template={template}
          isSelected={selectedTemplateId === template.id}
          onSelect={onSelect}
          onUse={onUse}
          onExpandShape={onExpandShape}
          onCustomize={onCustomize}
          onDelete={onDelete}
          canDelete={blogTemplatesService.canDelete(template)}
        />
      ))}
    </div>
  );

  const renderListSection = (templates: BlogTemplateSummary[]) => (
    <div className="flex flex-col gap-1">
      {templates.map((template) => (
        <TemplateListItem
          key={template.id}
          template={template}
          isSelected={selectedTemplateId === template.id}
          onSelect={onSelect}
        />
      ))}
    </div>
  );

  const emptyYoursNote = (
    <p className="text-xs text-muted-foreground py-3">
      Make your own copy of any standard template to tune it for this client
    </p>
  );

  return (
    <div className="flex flex-col min-h-0 overflow-hidden flex-1">
      {/* Search Bar */}
      <div className="flex-shrink-0 mb-3 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => onSearchChange('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Results count */}
      <div className="flex-shrink-0 mb-2">
        <span className="text-xs text-muted-foreground">
          {totalCount} template{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Template List - Scrollable in split view, grouped grid otherwise */}
      {splitMode ? (
        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-1 pr-2">
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <Skeleton className="h-8 w-8 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-3/4 mb-1" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))
            ) : (
              <>
                {renderGroupHeader('Yours', yoursTemplates.length)}
                {yoursTemplates.length > 0 ? renderListSection(yoursTemplates) : emptyYoursNote}
                <div className="mt-3" />
                {renderGroupHeader('Standard', standardTemplates.length)}
                {renderListSection(standardTemplates)}
              </>
            )}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
              <img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
              <p className="text-sm text-muted-foreground animate-pulse transition-all duration-500">
                {LOADING_MESSAGES[loadingMsgIndex]}
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={onRetry}>
                Retry
              </Button>
            </div>
          ) : totalCount > 0 ? (
            <div className="flex flex-col gap-5">
              {(() => {
                // S3 / CONSULTS §1: group by recipe class (comparison happens
                // WITHIN class; the cross-class size contrast is removed
                // structurally). Yours-first order is preserved within each
                // class; missing/malformed floors degrade to Standard.
                const classGroups = groupByGalleryClass([
                  ...yoursTemplates,
                  ...standardTemplates,
                ]);
                return (
                  <>
                    {classGroups.compact.length > 0 && (
                      <div data-class-group="compact">
                        {renderGroupHeader(
                          GALLERY_STRINGS.classes.compact.header,
                          classGroups.compact.length,
                          GALLERY_STRINGS.classes.compact.fitness,
                        )}
                        {renderCardGrid(classGroups.compact)}
                      </div>
                    )}
                    <div data-class-group="standard">
                      {renderGroupHeader(
                        GALLERY_STRINGS.classes.standard.header,
                        classGroups.standard.length,
                        GALLERY_STRINGS.classes.standard.fitness,
                      )}
                      {classGroups.standard.length > 0
                        ? renderCardGrid(classGroups.standard)
                        : emptyYoursNote}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-1">No templates found</p>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filters
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClearFilters}>
                  Clear Filters
                </Button>
                <Button onClick={onCreateTemplate}>
                  <Zap className="h-4 w-4 mr-2" />
                  Create Auto Template
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
